// ─── Recipe Health helpers ──────────────────────────────────────────────────
//
// Single source of truth for the recipe-ingredient linkage state machine.
// Every code path that mutates `recipe_ingredients.link_status` (drawer,
// queue, bulk action, manual-cost flow) MUST go through these helpers so
// the audit log + parent menu_item.pricing_health stay in sync.
//
// Architectural parallel: this file is to Recipe Health what
// `src/lib/parseGuardrails.js` is to the Data Review queue. Same shape:
// pure helpers + a Postgres audit log table + denormalized state column on
// the canonical entity.
//
// ── unit_cost / manual_unit_cost denormalization rule ──────────────────────
// recipe_ingredients.unit_cost is the SINGLE column the existing live-recalc
// pipeline reads from. We do NOT introduce a fork. Instead:
//
//   link_status='linked'                → unit_cost stays driven by
//                                          food_items.current_price (existing
//                                          behavior, untouched).
//   link_status='unlinked_manual_cost'  → unit_cost = manual_unit_cost
//                                          (denormalized — same value lives
//                                          in both columns; manual_unit_cost
//                                          is the durable "operator-set"
//                                          marker so we can re-render the
//                                          editor and tell which value won).
//   link_status='unlinked_no_cost'      → unit_cost = 0, manual_unit_cost = NULL.
//   link_status='pending_review'        → unit_cost retains whatever value
//                                          the prior matcher computed; the
//                                          state is a UI marker, not a
//                                          cost-flow change.
//
// IMPORTANT FOR FUTURE MAINTAINERS:
// Keep unit_cost === manual_unit_cost when link_status='unlinked_manual_cost'.
// If you split them ("unit_cost is what we paid, manual_unit_cost is what
// the operator entered") you'll silently break live-recalc on every other
// surface that reads unit_cost as the authoritative cost. Update both
// columns together.
//
// `is_ai_generated` is a separate boolean ("this row was AI-suggested at
// upload time") and is NOT touched by these helpers. It stays as historical
// provenance, distinct from link_status (the operator-confirmation state).

import { supabase } from '../supabase';

// ── Pure: derive parent menu_item.pricing_health from its ingredients ────
// Mirrors the SQL CASE expression in the migration. Keep these in sync —
// if the rules change, update both this function and the SQL.
//
// Order of precedence (worst state wins):
//   1. zero ingredients               → 'no_recipe'
//   2. any pending_review ingredient  → 'has_pending_review'
//   3. any unlinked_no_cost ingredient → 'has_unlinked'
//   4. otherwise                      → 'healthy'
//
// 'incomplete' is a valid CHECK constraint value reserved for future use
// when we want to mark a menu_item as "structurally incomplete" beyond
// just unlinked ingredients (e.g. missing menu_price). Not currently
// emitted by this function; kept in the constraint for forward-compat.
//
// `unlinked_manual_cost` rows do NOT elevate pricing_health — manual cost
// is operator-blessed, just stale-protected.
export function computePricingHealth(ingredients) {
  if (!ingredients || ingredients.length === 0) return 'no_recipe';
  const hasPending = ingredients.some(i => i.link_status === 'pending_review');
  if (hasPending) return 'has_pending_review';
  const hasUnlinked = ingredients.some(i => i.link_status === 'unlinked_no_cost');
  if (hasUnlinked) return 'has_unlinked';
  return 'healthy';
}

// Recompute and persist pricing_health for a single menu_item. Reads the
// current set of ingredients from the DB (don't trust passed-in stale
// state) and runs the derivation.
export async function refreshMenuItemHealth(companyId, menuItemId) {
  if (!menuItemId) return;
  const { data: ings } = await supabase
    .from('recipe_ingredients')
    .select('link_status')
    .eq('company_id', companyId)
    .eq('menu_item_id', menuItemId);
  const health = computePricingHealth(ings || []);
  await supabase
    .from('menu_items')
    .update({ pricing_health: health, updated_at: new Date().toISOString() })
    .eq('id', menuItemId)
    .eq('company_id', companyId);
  return health;
}

// Recompute pricing_health for every menu_item in a company. Used on the
// Recipe Health page mount to make sure the sidebar / chips are in sync
// after any direct DB tampering.
export async function refreshAllMenuItemHealth(companyId) {
  if (!companyId) return;
  const { data: items } = await supabase
    .from('menu_items')
    .select('id')
    .eq('company_id', companyId);
  for (const it of (items || [])) {
    await refreshMenuItemHealth(companyId, it.id);
  }
}

// ── Audit attribution boundary check ──────────────────────────────────────
// Every helper that writes to recipe_health_log or recipe_ingredients.linked_by
// REQUIRES userEmail. Silent null writes hide upstream bugs (caller forgot
// to thread the auth session through) and corrupt the audit trail. We throw
// loudly so the bug surfaces in dev / Sentry instead of producing
// untraceable production data.
function assertUserEmail(userEmail, helperName) {
  if (typeof userEmail !== 'string' || !userEmail.trim()) {
    throw new Error(`${helperName}: userEmail is required (got ${userEmail === undefined ? 'undefined' : userEmail === null ? 'null' : typeof userEmail}). The caller must pass the signed-in user's email — usually via supabase.auth.getUser().`);
  }
}

// ── Audit log writer ──────────────────────────────────────────────────────
// Every state transition writes a recipe_health_log row. Idempotent in the
// sense that re-running an event is captured as a separate log row (we
// don't try to dedupe — the timeline matters for forensics).
async function appendLog({ companyId, recipeIngredientId, menuItemId, eventType, priorState, newState, performedBy }) {
  await supabase.from('recipe_health_log').insert({
    company_id: companyId,
    recipe_ingredient_id: recipeIngredientId || null,
    menu_item_id: menuItemId || null,
    event_type: eventType,
    prior_state: priorState ? JSON.parse(JSON.stringify(priorState)) : null,
    new_state: newState ? JSON.parse(JSON.stringify(newState)) : null,
    performed_by: performedBy || null,
  });
}

// Snapshot a recipe_ingredient row for audit log entries. Pulls the fields
// that drive linkage decisions; ignores noise (created_at, etc).
function snapshot(row) {
  if (!row) return null;
  return {
    id: row.id,
    food_item_id: row.food_item_id,
    link_status: row.link_status,
    unit_cost: row.unit_cost,
    line_cost: row.line_cost,
    manual_unit_cost: row.manual_unit_cost,
    match_confidence: row.match_confidence,
    ingredient_name: row.ingredient_name,
  };
}

async function fetchIngredient(id) {
  const { data } = await supabase
    .from('recipe_ingredients')
    .select('*')
    .eq('id', id)
    .single();
  return data;
}

// ── State transitions ────────────────────────────────────────────────────
// Each helper:
//   1. Reads the current row (for prior_state snapshot + parent menu_item_id)
//   2. Mutates recipe_ingredients
//   3. Writes recipe_health_log
//   4. Refreshes parent menu_item.pricing_health
//   5. Returns the new ingredient row for caller refetch convenience
//
// Live recalc note: after these mutations land, the caller (drawer / queue
// page) is responsible for triggering its own refetch of menu_items so the
// plate_cost and margin_pct numbers reflect the new ingredient state. We
// don't bake plate_cost recompute into this layer because the existing
// MenuProfitability.fetchAll() does it as part of its normal flow and we
// want to keep that path single-entry.

// User confirms the AI-suggested link is correct. pending_review → linked.
// Records prior food_item_id in case operator wants to undo via re-link
// (though re-link via a different food_item_id is just a normal user_relinked
// event, not a special undo).
//
// userEmail (required): caller passes the signed-in user's email so
// linked_by + recipe_health_log.performed_by populate correctly. Throws
// at the boundary if missing — silent null writes hide upstream bugs.
export async function confirmLink({ companyId, recipeIngredientId, userEmail }) {
  assertUserEmail(userEmail, 'confirmLink');
  const prior = await fetchIngredient(recipeIngredientId);
  if (!prior) return null;
  const { data: updated } = await supabase
    .from('recipe_ingredients')
    .update({
      link_status: 'linked',
      linked_at: new Date().toISOString(),
      linked_by: userEmail,
    })
    .eq('id', recipeIngredientId)
    .eq('company_id', companyId)
    .select()
    .single();
  await appendLog({
    companyId,
    recipeIngredientId,
    menuItemId: prior.menu_item_id,
    eventType: 'user_confirmed',
    priorState: snapshot(prior),
    newState: snapshot(updated),
    performedBy: userEmail,
  });
  await refreshMenuItemHealth(companyId, prior.menu_item_id);
  return updated;
}

// Operator picks a different food_item from the catalog. Updates
// food_item_id, recomputes unit_cost from the new item's price, transitions
// to 'linked'. Either pending_review → linked OR linked → linked
// (re-link). Both paths log as 'user_relinked' (the user_confirmed event
// is reserved for "AI suggestion accepted as-is", which is a different
// signal we want to be able to count separately).
// userEmail (required): see confirmLink doc. Throws if missing.
export async function relinkToFoodItem({ companyId, recipeIngredientId, foodItemId, newUnitCost, matchConfidence, userEmail }) {
  assertUserEmail(userEmail, 'relinkToFoodItem');
  const prior = await fetchIngredient(recipeIngredientId);
  if (!prior) return null;
  const qty = Number(prior.quantity) || 0;
  const lineC = (Number(newUnitCost) || 0) * qty;
  const { data: updated } = await supabase
    .from('recipe_ingredients')
    .update({
      food_item_id: foodItemId,
      link_status: 'linked',
      unit_cost: Number(newUnitCost) || 0,
      line_cost: lineC,
      manual_unit_cost: null, // clear any prior manual override
      match_confidence: matchConfidence != null ? matchConfidence : null,
      linked_at: new Date().toISOString(),
      linked_by: userEmail,
    })
    .eq('id', recipeIngredientId)
    .eq('company_id', companyId)
    .select()
    .single();
  await appendLog({
    companyId,
    recipeIngredientId,
    menuItemId: prior.menu_item_id,
    eventType: 'user_relinked',
    priorState: snapshot(prior),
    newState: snapshot(updated),
    performedBy: userEmail,
  });
  await refreshMenuItemHealth(companyId, prior.menu_item_id);
  return updated;
}

// Operator sets a manual unit cost (no catalog match). Stores the value in
// BOTH manual_unit_cost (durable marker) and unit_cost (live-recalc reads
// this column). Clears food_item_id since manual cost takes precedence over
// any stale link.
// userEmail (required): see confirmLink doc. Throws if missing.
// Note linked_by is cleared here because manual cost is NOT a link — but
// performed_by on the log row still captures who set it.
export async function setManualCost({ companyId, recipeIngredientId, manualUnitCost, userEmail }) {
  assertUserEmail(userEmail, 'setManualCost');
  const prior = await fetchIngredient(recipeIngredientId);
  if (!prior) return null;
  const value = Number(manualUnitCost) || 0;
  const qty = Number(prior.quantity) || 0;
  const wasManualBefore = prior.link_status === 'unlinked_manual_cost';
  const { data: updated } = await supabase
    .from('recipe_ingredients')
    .update({
      link_status: 'unlinked_manual_cost',
      food_item_id: null,
      unit_cost: value,
      line_cost: value * qty,
      manual_unit_cost: value,
      match_confidence: null,
      linked_at: null,
      linked_by: null,
    })
    .eq('id', recipeIngredientId)
    .eq('company_id', companyId)
    .select()
    .single();
  await appendLog({
    companyId,
    recipeIngredientId,
    menuItemId: prior.menu_item_id,
    eventType: wasManualBefore ? 'manual_cost_changed' : 'manual_cost_set',
    priorState: snapshot(prior),
    newState: snapshot(updated),
    performedBy: userEmail,
  });
  await refreshMenuItemHealth(companyId, prior.menu_item_id);
  return updated;
}

// Operator declares "no match in catalog, leave unpriced." Zeros out the
// cost contribution. Useful for ingredients we'll never have invoice data
// for (water, salt mixes, house seasonings).
// userEmail (required): see confirmLink doc. Throws if missing.
export async function markUnlinked({ companyId, recipeIngredientId, userEmail }) {
  assertUserEmail(userEmail, 'markUnlinked');
  const prior = await fetchIngredient(recipeIngredientId);
  if (!prior) return null;
  const { data: updated } = await supabase
    .from('recipe_ingredients')
    .update({
      link_status: 'unlinked_no_cost',
      food_item_id: null,
      unit_cost: 0,
      line_cost: 0,
      manual_unit_cost: null,
      match_confidence: null,
      linked_at: null,
      linked_by: null,
    })
    .eq('id', recipeIngredientId)
    .eq('company_id', companyId)
    .select()
    .single();
  await appendLog({
    companyId,
    recipeIngredientId,
    menuItemId: prior.menu_item_id,
    eventType: 'marked_unlinked',
    priorState: snapshot(prior),
    newState: snapshot(updated),
    performedBy: userEmail,
  });
  await refreshMenuItemHealth(companyId, prior.menu_item_id);
  return updated;
}

// Bulk-confirm a list of ingredient IDs as 'linked' (their existing
// food_item_id stays untouched — this is the "yes, the AI got it right"
// path). Single log row with the affected IDs in new_state JSONB so the
// audit trail can replay batch operations without spamming the log.
// userEmail (required): see confirmLink doc. Throws if missing.
export async function bulkConfirm({ companyId, recipeIngredientIds, userEmail }) {
  assertUserEmail(userEmail, 'bulkConfirm');
  if (!recipeIngredientIds || recipeIngredientIds.length === 0) return [];
  // Fetch prior state in one query for the audit log.
  const { data: priorRows } = await supabase
    .from('recipe_ingredients')
    .select('id, menu_item_id, food_item_id, link_status, unit_cost, line_cost, manual_unit_cost, ingredient_name')
    .in('id', recipeIngredientIds)
    .eq('company_id', companyId);

  // Single UPDATE for all rows.
  const { data: updated } = await supabase
    .from('recipe_ingredients')
    .update({
      link_status: 'linked',
      linked_at: new Date().toISOString(),
      linked_by: userEmail,
    })
    .in('id', recipeIngredientIds)
    .eq('company_id', companyId)
    .select();

  // Single log row capturing the batch.
  await appendLog({
    companyId,
    recipeIngredientId: null,
    menuItemId: null,
    eventType: 'bulk_confirmed',
    priorState: { ingredients: (priorRows || []).map(snapshot) },
    newState: { ingredient_ids: recipeIngredientIds, count: (updated || []).length },
    performedBy: userEmail,
  });

  // Refresh pricing_health for every distinct menu_item touched by the batch.
  const distinctMenuItemIds = Array.from(new Set((priorRows || []).map(r => r.menu_item_id).filter(Boolean)));
  for (const miId of distinctMenuItemIds) {
    await refreshMenuItemHealth(companyId, miId);
  }
  return updated || [];
}

// ── Margin attribution computation ────────────────────────────────────────
// "Biggest cost driver this month" line on the recipe drawer. Computed
// in-memory at drawer open time off invoice_line_items + the menu_item's
// linked ingredients. No precomputed table for v1.
//
// Returns:
//   null                           — no significant movement (don't render strip)
//   { ingredient, currentPerUnit, priorPerUnit, deltaPerPlate, deltaPctOfPlate, direction }
//
// Threshold: render only when |deltaPerPlate| > 2% of plate_cost OR
// |deltaPerPlate| > $0.05 absolute. Either condition is enough; both small
// is below noise.
export function computeMarginAttribution({ ingredients, foodItemPriorPrices, plateCost }) {
  if (!ingredients || ingredients.length === 0) return null;
  if (!foodItemPriorPrices) return null;

  let best = null;
  for (const ing of ingredients) {
    if (ing.link_status !== 'linked') continue;
    if (!ing.food_item_id) continue;
    const prior = foodItemPriorPrices[ing.food_item_id];
    if (prior == null || !isFinite(prior) || prior <= 0) continue;
    const current = Number(ing.unit_cost);
    if (!isFinite(current) || current <= 0) continue;
    const qty = Number(ing.quantity) || 0;
    if (qty <= 0) continue;
    const deltaPerPlate = (current - prior) * qty;
    const deltaPct = plateCost > 0 ? Math.abs(deltaPerPlate) / plateCost : 0;
    const significant = deltaPct > 0.02 || Math.abs(deltaPerPlate) > 0.05;
    if (!significant) continue;
    if (!best || Math.abs(deltaPerPlate) > Math.abs(best.deltaPerPlate)) {
      best = {
        ingredient: ing,
        currentPerUnit: current,
        priorPerUnit: prior,
        deltaPerPlate,
        deltaPctOfPlate: plateCost > 0 ? (deltaPerPlate / plateCost) * 100 : null,
        direction: deltaPerPlate > 0 ? 'up' : 'down',
      };
    }
  }
  return best;
}

// Fetch the trailing 30-day median price_per_lb (or unit_price for non-weight
// items) for each food_item_id in the list. Used by computeMarginAttribution
// as the "prior" baseline. Single batched query.
//
// Returns: { [food_item_id]: priorPrice } where priorPrice is in the same
// unit as recipe_ingredients.unit_cost (i.e. recipe-unit denominated). For
// catch-weight items we use price_per_lb when present; otherwise unit_price.
// This matches the existing MenuProfitability.refreshIngredientCost
// behavior so the comparison is apples-to-apples.
export async function fetchPriorPrices({ companyId, foodItemIds, asOfDays = 30 }) {
  if (!foodItemIds || foodItemIds.length === 0) return {};
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - asOfDays);
  const start = new Date(cutoff); start.setDate(start.getDate() - 30); // window: 30 days ending 30 days ago
  const startIso = start.toISOString().split('T')[0];
  const endIso = cutoff.toISOString().split('T')[0];

  const { data: lines } = await supabase
    .from('invoice_line_items')
    .select('food_item_id, unit_price, price_per_lb')
    .eq('company_id', companyId)
    .in('food_item_id', foodItemIds)
    .gte('invoice_date', startIso)
    .lte('invoice_date', endIso)
    .range(0, 49999);

  const byItem = {};
  for (const l of (lines || [])) {
    if (!byItem[l.food_item_id]) byItem[l.food_item_id] = { perLb: [], perUnit: [] };
    if (l.price_per_lb != null && l.price_per_lb > 0) byItem[l.food_item_id].perLb.push(Number(l.price_per_lb));
    if (l.unit_price != null && l.unit_price > 0) byItem[l.food_item_id].perUnit.push(Number(l.unit_price));
  }

  const median = (arr) => {
    if (!arr || arr.length === 0) return null;
    const sorted = arr.slice().sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
  };

  const out = {};
  for (const id of Object.keys(byItem)) {
    out[id] = median(byItem[id].perLb) || median(byItem[id].perUnit) || null;
  }
  return out;
}

// ── String-distance fuzzy match for the "Change link" / "Find match" modal.
// Lightweight (no fancy NLP, no embedding) — Commit 2 will add a confidence
// scorer. v1 scoring: token overlap + substring match + length penalty.
// Good enough to get the right item to the top 5 of the dropdown.
export function fuzzyMatchFoodItems(query, foodItems, limit = 5) {
  if (!query || !query.trim() || !foodItems) return [];
  const q = query.trim().toLowerCase();
  const qTokens = new Set(q.split(/[\s\-_/(),]+/).filter(t => t.length >= 2));
  const scored = foodItems.map(fi => {
    const name = (fi.normalized_name || fi.name || '').toLowerCase();
    if (!name) return { fi, score: 0 };
    let score = 0;
    // Substring boost — "beef chuck" inside "BEEF CHUCK SHOULDER CLOD" is a strong signal.
    if (name.includes(q)) score += 0.5;
    // Token overlap (Jaccard-ish, skewed toward query token coverage).
    const nameTokens = new Set(name.split(/[\s\-_/(),]+/).filter(t => t.length >= 2));
    let overlap = 0;
    for (const t of qTokens) if (nameTokens.has(t)) overlap++;
    if (qTokens.size > 0) score += (overlap / qTokens.size) * 0.4;
    // Slight length-penalty so "BEEF" matches "BEEF" before "BEEF CHUCK SHOULDER..."
    score += Math.max(0, 0.1 - Math.abs(name.length - q.length) / 200);
    return { fi, score };
  });
  scored.sort((a, b) => b.score - a.score);
  return scored
    .filter(r => r.score > 0)
    .slice(0, limit)
    .map(r => ({ ...r.fi, _matchScore: r.score }));
}
